import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import Tooltip from "../common/Tooltip";
import { motion } from "framer-motion";
import { ProfileActivityCalendarSkeleton } from "./profileSkeletons";

const MotionBox = motion(Box);

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ACTIVITY_DAYS = 90;
const WEEKS_TO_SHOW = Math.ceil(ACTIVITY_DAYS / 7);

function toLocalDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function normalizeActivityMatches(matches) {
    if (!Array.isArray(matches)) {
        return [];
    }

    return matches
        .map((match) => {
            const raw = match?.createdAt ?? match?.timestamp;
            const n = Number(raw);
            if (!Number.isFinite(n) || n <= 0) {
                return null;
            }

            const createdAt = n < 1e12 ? Math.floor(n) : Math.floor(n / 1000);
            return { createdAt };
        })
        .filter(Boolean);
}

function getIntensity(count, max) {
    if (count === 0) return 0;
    if (max === 0) return 0;
    return Math.ceil((count / max) * 4);
}

function buildCalendarGrid(matches, weeksToShow) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - mondayOffset);

    const startDate = new Date(lastMonday);
    startDate.setDate(lastMonday.getDate() - (weeksToShow - 1) * 7);

    const countByDate = {};
    for (const match of matches) {
        const createdAt = Number(match.createdAt);
        if (!Number.isFinite(createdAt) || createdAt <= 0) {
            continue;
        }

        const d = new Date(createdAt * 1000);
        const key = toLocalDateKey(d);
        countByDate[key] = (countByDate[key] || 0) + 1;
    }

    const weeks = [];
    let current = new Date(startDate);

    for (let w = 0; w < weeksToShow; w++) {
        const week = [];
        for (let d = 0; d < 7; d++) {
            const key = toLocalDateKey(current);
            const isFuture = current > today;
            week.push({
                date: new Date(current),
                key,
                count: isFuture ? -1 : (countByDate[key] || 0),
                isFuture,
            });
            current.setDate(current.getDate() + 1);
        }
        weeks.push(week);
    }

    const allCounts = Object.values(countByDate);
    const max = allCounts.length > 0 ? Math.max(...allCounts) : 1;

    return { weeks, max };
}

function buildMonthLabels(weeks) {
    const labels = [];
    let lastMonth = null;

    weeks.forEach((week, i) => {
        const firstDay = week[0].date;
        const month = firstDay.getMonth();
        if (month !== lastMonth) {
            labels.push({ weekIndex: i, label: firstDay.toLocaleString("en-US", { month: "short" }) });
            lastMonth = month;
        }
    });

    return labels;
}

const CELL_SIZE = 17;
const CELL_GAP = 5;

function formatActivityDate(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

function ActivityCellTooltipContent({ date, count }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <Typography
                sx={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: colors.text.primary,
                    lineHeight: 1.2,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {formatActivityDate(date)}
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: colors.text.muted,
                    lineHeight: 1.2,
                }}
            >
                {count} {count === 1 ? "match" : "matches"}
            </Typography>
        </Box>
    );
}

ActivityCellTooltipContent.propTypes = {
    date: PropTypes.instanceOf(Date).isRequired,
    count: PropTypes.number.isRequired,
};

function ActivityCell({ cell, backgroundColor }) {
    const cellBox = (
        <MotionBox
            component="span"
            whileHover={
                cell.isFuture
                    ? {}
                    : {
                        y: -1,
                        scale: 1.12,
                    }
            }
            transition={{
                type: "spring",
                stiffness: 320,
                damping: 18,
            }}
            sx={{
                display: "inline-flex",
                width: CELL_SIZE,
                height: CELL_SIZE,
                borderRadius: "3px",
                mr: `${CELL_GAP}px`,
                flexShrink: 0,
                backgroundColor,
                transition: "opacity 0.15s",
                cursor: cell.isFuture ? "default" : "pointer",
                "&:hover": cell.isFuture
                    ? {}
                    : {
                        opacity: 0.8,
                    },
            }}
        />
    );

    if (cell.isFuture) {
        return cellBox;
    }

    return (
        <Tooltip
            title={<ActivityCellTooltipContent date={cell.date} count={cell.count} />}
            placement="top"
        >
            {cellBox}
        </Tooltip>
    );
}

ActivityCell.propTypes = {
    cell: PropTypes.shape({
        date: PropTypes.instanceOf(Date).isRequired,
        count: PropTypes.number.isRequired,
        isFuture: PropTypes.bool.isRequired,
    }).isRequired,
    backgroundColor: PropTypes.string.isRequired,
};

export default function OverviewActivityCalendar({ matches = [], isLoading = false }) {
    const normalizedMatches = useMemo(
        () => normalizeActivityMatches(matches),
        [matches]
    );

    const { weeks, max } = useMemo(
        () => buildCalendarGrid(normalizedMatches, WEEKS_TO_SHOW),
        [normalizedMatches]
    );
    const monthLabels = useMemo(() => buildMonthLabels(weeks), [weeks]);

    const totalMatches = useMemo(() => {
        const cutoff = new Date();
        cutoff.setHours(0, 0, 0, 0);
        cutoff.setDate(cutoff.getDate() - ACTIVITY_DAYS);
        const cutoffSec = Math.floor(cutoff.getTime() / 1000);
        return normalizedMatches.filter((m) => Number(m.createdAt) >= cutoffSec).length;
    }, [normalizedMatches]);
    const cellColor = (count, isFuture) => {
        if (isFuture || count < 0) return "transparent";
        if (count === 0) return alpha(colors.text.muted, 0.12);
        const intensity = Math.min(4, Math.max(1, getIntensity(count, max)));
        const opacities = [0.25, 0.45, 0.7, 1.0];
        return alpha(colors.primary.main, opacities[intensity - 1]);
    };

    if (isLoading) {
        return <ProfileActivityCalendarSkeleton />;
    }

    return (
        <Box>
            <Box sx={{ overflowX: "auto", pb: "0.25rem" }}>
                <Box sx={{ display: "inline-flex", flexDirection: "column", gap: 0 }}>
                    {/* Month labels row */}
                    <Box sx={{ display: "flex", ml: `${CELL_SIZE + CELL_GAP + 6}px`, mb: "4px" }}>
                        {weeks.map((_, i) => {
                            const label = monthLabels.find(l => l.weekIndex === i);
                            return (
                                <Box
                                    key={i}
                                    sx={{
                                        width: CELL_SIZE,
                                        mr: `${CELL_GAP}px`,
                                        flexShrink: 0,
                                    }}
                                >
                                    {label && (
                                        <Typography sx={{ fontSize: "0.62rem", color: colors.text.muted, lineHeight: 1, whiteSpace: "nowrap" }}>
                                            {label.label}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Grid rows */}
                    {DAYS.map((day, dayIndex) => (
                        <Box key={day} sx={{ display: "flex", alignItems: "center", mb: `${CELL_GAP}px` }}>
                            <Typography
                                sx={{
                                    fontSize: "0.62rem",
                                    color: colors.text.muted,
                                    width: CELL_SIZE + 6,
                                    mr: "6px",
                                    lineHeight: 1,
                                    flexShrink: 0,
                                }}
                            >
                                {day}
                            </Typography>
                            {weeks.map((week, weekIndex) => {
                                const cell = week[dayIndex];
                                return (
                                    <ActivityCell
                                        key={weekIndex}
                                        cell={cell}
                                        backgroundColor={cellColor(cell.count, cell.isFuture)}
                                    />
                                );
                            })}                            
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "1rem",
                    mt: "0.75rem",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.75rem",
                        color: colors.text.muted,
                        fontVariantNumeric: "tabular-nums",
                        flexShrink: 0,
                    }}
                >
                    {totalMatches} {totalMatches === 1 ? "match" : "matches"} played
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <Typography sx={{ fontSize: "0.7rem", color: colors.text.muted, mr: "4px" }}>
                        Less
                    </Typography>
                    {[0, 1, 2, 3, 4].map((i) => (
                        <Box
                            key={i}
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "2px",
                                backgroundColor:
                                    i === 0
                                        ? alpha(colors.text.muted, 0.12)
                                        : alpha(colors.primary.main, [0.25, 0.45, 0.7, 1.0][i - 1]),
                            }}
                        />
                    ))}
                    <Typography sx={{ fontSize: "0.7rem", color: colors.text.muted, ml: "4px" }}>
                        More
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
OverviewActivityCalendar.propTypes = {
    matches: PropTypes.arrayOf(
        PropTypes.shape({
            createdAt: PropTypes.number,
        })
    ),
    isLoading: PropTypes.bool,
};